import { type HeaderContext, type RowData, type SortingState } from '@tanstack/react-table'
import { ChevronUpIcon } from 'lucide-react'
import React from 'react'

import { handleTanstackSortingStateForSingleColumn } from '../../../utils'

type SetPagination = React.Dispatch<
  React.SetStateAction<{
    pageIndex: number;
    pageSize: number;
  }>
>

export type SetSorting = React.Dispatch<React.SetStateAction<SortingState>>

// Need to declare the TableMeta type with its specific fields for the methods to be passed around as part of the meta object.
// https://tanstack.com/table/v8/docs/api/core/table#meta
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    setPagination?: SetPagination;
    setSorting?: SetSorting;
    defaultSortingState?: SortingState;
    disableSortingForColumns?: { [key: string]: boolean };
  }
}

interface HeaderProps<T>
  extends HeaderContext<T, number | string | undefined> {
  headerText: string;
  align?: 'left' | 'right';
  className?: string;
  leftIcon?: React.ReactNode;
}

export const SortableHeader = <T, >({ headerText, table, column, header, align = 'left', className, leftIcon }: HeaderProps<T>) => {
  const sorted = table
    .getState()
    .sorting?.find((el: { id: string; desc: boolean }) => el.id === column.id)

  const setSorting = table.options.meta?.setSorting as SetSorting
  const defaultSortingState = table.options.meta?.defaultSortingState || [] as SortingState
  const disableSortingForColumns = table.options.meta?.disableSortingForColumns || {} as Record<string, boolean>

  const alignClass = align === 'right' ? 'text-right justify-end' : 'text-left'

  return (
    <div className={`flex items-center ${alignClass}`}>
      {leftIcon && (
        <div className='mr-2'>
          {leftIcon}
        </div>
      )}
      <div
        className={`flex items-center cursor-pointer select-none ${className}`}
        onClick={() => setSorting && !disableSortingForColumns[header.id] && handleTanstackSortingStateForSingleColumn(header.id, setSorting, defaultSortingState)}
      >
        <span className={`${alignClass}`}>
          {headerText}
        </span>
        <span className={`${!sorted ? 'hidden' : ''} pl-2`}>
          <span
            className={`transition-transform flex items-center ${sorted?.desc ? 'rotate-180' : 'rotate-0'}`}
          >
            <ChevronUpIcon size={16} />
          </span>
        </span>
      </div>
    </div>
  )
}
